import type {
  PosterProductDataForProcessDesign,
  PosterPublishingOptionsReduxState,
  PublishingOptionStats,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {DisplayType, ProductType, SelectionType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {SubFlowType} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-compiled-sub-panel/digital-signage-compiled-sub-panel.types';
import type {DigitalSignageProviderSubFlow} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.types';
import type {OrderDetailsForRedux} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-panel.types';
import type {SocialMediaResponse, WebLinkResponse} from '@Panels/poster-publishing-options-panel/components/publishing-social-media-panel/publishing-social-media-panel.types';
import {
  getDefaultOrderDetails,
  getDefaultPosterProductDataForProcessDesign,
  getDefaultSocialMediaLinks,
  getDefaultWebLinks,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';

const initialState: PosterPublishingOptionsReduxState = {
  displayType: DisplayType.MODAL,
  selectionType: SelectionType.DEFAULT_DISPLAY,
  subFlowType: SubFlowType.DEFAULT,
  orderDetails: getDefaultOrderDetails(),
  premiumProductDataForPrint: getDefaultPosterProductDataForProcessDesign(),
  freebieProductData: getDefaultPosterProductDataForProcessDesign(),
  premiumProductDataForPoster: getDefaultPosterProductDataForProcessDesign(),
  productType: ProductType.DEFAULT,
  socialMediaLinks: getDefaultSocialMediaLinks(),
  webLinks: getDefaultWebLinks(),
  showError: false,
  errorMessage: 'Some error occured. Please try again.',
  showLoader: false,
  hashedId: '',
  posterName: '',
  posterPreviewUrl: '',
  posterPageHashedId: '',
  publishingOptionStats: null,
  // A signage provider queued by a Recommended-tile deep-link; it auto-starts once page data is ready, unlike the manually-selected active subFlowType.
  pendingSignageSubFlow: null,
};

const posterPublishingOptionsSlice = createSlice({
  name: 'posterPublishingOptionsSlice',
  initialState,
  reducers: {
    setDisplayType: (state, action: PayloadAction<DisplayType>): void => {
      state.displayType = action.payload;
    },

    setSelectionType: (state, action: PayloadAction<SelectionType>): void => {
      state.selectionType = action.payload;
    },

    setOrderDetails: (state, action: PayloadAction<OrderDetailsForRedux>): void => {
      state.orderDetails.progressPercentage = action.payload.progressPercentage;
      state.orderDetails.estimatedTimeInSeconds = action.payload.estimatedTimeInSeconds;
      state.orderDetails.timeElapsedInSeconds = action.payload.timeElapsedInSeconds;
    },

    setSocialMediaLinks: (state, action: PayloadAction<SocialMediaResponse>): void => {
      state.socialMediaLinks.fb = action.payload.fb;
      state.socialMediaLinks.posterName = action.payload.posterName;
      state.socialMediaLinks.pt = action.payload.pt;
      state.socialMediaLinks.ptUrl = action.payload.ptUrl;
      state.socialMediaLinks.tw = action.payload.tw;
    },

    setWebLinksData: (state, action: PayloadAction<Partial<WebLinkResponse>>): void => {
      if (action.payload.isPublishedLink !== undefined) {
        state.webLinks.isPublishedLink = action.payload.isPublishedLink;
      }
      if (action.payload.isUrlDisabled !== undefined) {
        state.webLinks.isUrlDisabled = action.payload.isUrlDisabled;
      }
      if (action.payload.shareableUrl !== undefined) {
        state.webLinks.shareableUrl = action.payload.shareableUrl;
      }
      if (action.payload.qrHostedShareableUrl !== undefined) {
        state.webLinks.qrHostedShareableUrl = action.payload.qrHostedShareableUrl;
      }
      if (action.payload.qrTemplateHashedId !== undefined) {
        state.webLinks.qrTemplateHashedId = action.payload.qrTemplateHashedId;
      }
      if (action.payload.isQrHostedLinkPublished !== undefined) {
        state.webLinks.isQrHostedLinkPublished = action.payload.isQrHostedLinkPublished;
      }
      if (action.payload.hasUserSeenQrOption !== undefined) {
        state.webLinks.hasUserSeenQrOption = action.payload.hasUserSeenQrOption;
      }
      if (action.payload.publishingCap !== undefined) {
        state.webLinks.publishingCap = action.payload.publishingCap;
      }
      if (action.payload.embedPublishingCap !== undefined) {
        state.webLinks.embedPublishingCap = action.payload.embedPublishingCap;
      }
    },

    setOrderError: (state, action: PayloadAction<string>): void => {
      state.orderDetails.error = action.payload;
    },

    setErrorOnPublishPanel: (state, action: PayloadAction<boolean>): void => {
      state.showError = action.payload;
    },

    setErrorMessageForPublishing: (state, action: PayloadAction<string>): void => {
      state.errorMessage = action.payload;
    },

    setPremiumProductForPoster: (state, action: PayloadAction<PosterProductDataForProcessDesign>): void => {
      state.premiumProductDataForPoster = action.payload;
    },

    setPremiumProductForPrint: (state, action: PayloadAction<PosterProductDataForProcessDesign>): void => {
      state.premiumProductDataForPrint = action.payload;
    },

    setFreebieProduct: (state, action: PayloadAction<PosterProductDataForProcessDesign>): void => {
      state.freebieProductData = action.payload;
    },

    setProductType: (state, action: PayloadAction<ProductType>): void => {
      state.productType = action.payload;
    },

    setShowLoader: (state, action: PayloadAction<boolean>): void => {
      state.showLoader = action.payload;
    },

    digitalSignageFlow: (state, action: PayloadAction<DigitalSignageProviderSubFlow | undefined>): void => {
      state.selectionType = SelectionType.DIGITAL_SIGNAGE_DISPLAY;
      state.pendingSignageSubFlow = action.payload ?? null;
    },

    clearPendingSignageSubFlow: (state): void => {
      state.pendingSignageSubFlow = null;
    },

    shareToAppFlow: (state): void => {
      state.selectionType = SelectionType.SHARE_TO_APP;
    },

    postToSocialMediaFlow: (state): void => {
      state.selectionType = SelectionType.POST_TO_SOCIAL_MEDIA;
    },

    webpagePublishFlow: (state): void => {
      state.selectionType = SelectionType.PUBLISH_WEBPAGE_DISPLAY;
    },

    webpageEmbedFlow: (state): void => {
      state.selectionType = SelectionType.EMBED_WEBPAGE_DISPLAY;
    },

    socialMediaFlow: (state): void => {
      state.selectionType = SelectionType.SOCIAL_MEDIA_LINK;
    },

    subPanelFlow: (state): void => {
      state.selectionType = SelectionType.SUB_DISPLAY;
    },

    qrCodeFlow: (state): void => {
      state.selectionType = SelectionType.QR_CODE;
    },

    confirmationPanelFlow: (state): void => {
      state.selectionType = SelectionType.CONFIRMATION_DISPLAY;
    },

    emailMarketingCampaignFlow: (state): void => {
      state.selectionType = SelectionType.PUBLISH_EMAIL_CAMPAIGN;
    },

    createEventWebpageFlow: (state): void => {
      state.selectionType = SelectionType.CREATE_EVENT_WEBPAGE;
    },

    selectionTypeSubDisplay: (state, action: PayloadAction<SubFlowType>): void => {
      state.subFlowType = action.payload;
    },

    handleBackButtonClick: (state): void => {
      state.selectionType = SelectionType.DEFAULT_DISPLAY;
    },

    setErrorMessageForPublishingPanel: (state, action: PayloadAction<string>): void => {
      state.showError = true;
      state.errorMessage = action.payload;
    },

    resetPanelState: (state): void => {
      state.selectionType = SelectionType.DEFAULT_DISPLAY;
      state.subFlowType = SubFlowType.DEFAULT;
      state.showLoader = false;
      state.showError = false;
      state.orderDetails.error = '';
      state.posterPageHashedId = '';
      state.pendingSignageSubFlow = null;
    },

    setHashedId: (state, action: PayloadAction<string>): void => {
      state.hashedId = action.payload;
    },

    setPosterName: (state, action: PayloadAction<string>): void => {
      state.posterName = action.payload;
    },

    setPosterPreviewUrl: (state, action: PayloadAction<string>): void => {
      state.posterPreviewUrl = action.payload;
    },

    setPosterPageHashedId: (state, action: PayloadAction<string>): void => {
      state.posterPageHashedId = action.payload;
    },

    setPublishingOptionStats: (state, action: PayloadAction<PublishingOptionStats | null>): void => {
      state.publishingOptionStats = action.payload;
    },
  },
});

export const {
  setOrderDetails,
  setFreebieProduct,
  setDisplayType,
  setPremiumProductForPrint,
  setPremiumProductForPoster,
  setProductType,
  setOrderError,
  setErrorOnPublishPanel,
  setErrorMessageForPublishing,
  setShowLoader,
  selectionTypeSubDisplay,
  subPanelFlow,
  socialMediaFlow,
  webpageEmbedFlow,
  setSocialMediaLinks,
  setWebLinksData,
  shareToAppFlow,
  postToSocialMediaFlow,
  digitalSignageFlow,
  webpagePublishFlow,
  qrCodeFlow,
  confirmationPanelFlow,
  emailMarketingCampaignFlow,
  createEventWebpageFlow,
  handleBackButtonClick,
  setHashedId,
  resetPanelState,
  setErrorMessageForPublishingPanel,
  setPosterName,
  setPosterPreviewUrl,
  setSelectionType,
  setPosterPageHashedId,
  setPublishingOptionStats,
  clearPendingSignageSubFlow,
} = posterPublishingOptionsSlice.actions;
export const posterPublishingOptionsReducer = posterPublishingOptionsSlice.reducer;
